import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calculator, MapPin, Truck, Package, Plus, X, ChevronRight,
  AlertTriangle, CheckCircle2, Loader2, ArrowRight, Info, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function PincodeInput({ label, value, onChange, pincodeInfo, loading, side }) {
  const dotColor = side === "pickup" ? "bg-emerald-500" : "bg-red-500";
  const borderColor = pincodeInfo?.found
    ? side === "pickup" ? "border-emerald-400" : "border-red-400"
    : "border-slate-200";

  return (
    <div className="flex-1 min-w-[200px]">
      <div className={`relative border-2 rounded-lg p-3 transition-colors ${borderColor}`}>
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
          <input
            data-testid={`calc-${side}-pin`}
            className="flex-1 text-lg font-mono font-bold bg-transparent outline-none text-slate-800 placeholder-slate-300"
            value={value}
            onChange={e => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter Pincode"
            maxLength={6}
          />
          {pincodeInfo?.found && (
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              {pincodeInfo.state_code}
            </span>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
        {pincodeInfo?.found && (
          <p className="text-xs text-slate-500 pl-4.5 ml-4">
            {pincodeInfo.city}, {pincodeInfo.state}
          </p>
        )}
      </div>
      {pincodeInfo?.found === false && value.length === 6 && (
        <p className="text-xs text-red-500 mt-1 ml-1">Pincode not found</p>
      )}
    </div>
  );
}

function BoxRow({ box, index, onUpdate, onRemove, showRemove }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-400 text-xs w-6">#{index + 1}</span>
      <Input
        type="number" min="1" placeholder="Qty"
        className="w-16 h-8 text-xs"
        value={box.quantity}
        onChange={e => onUpdate(index, "quantity", parseInt(e.target.value) || 1)}
      />
      <Input
        type="number" min="0" step="0.1" placeholder="L"
        className="w-16 h-8 text-xs"
        value={box.length_cm}
        onChange={e => onUpdate(index, "length_cm", parseFloat(e.target.value) || 0)}
      />
      <span className="text-slate-300">x</span>
      <Input
        type="number" min="0" step="0.1" placeholder="B"
        className="w-16 h-8 text-xs"
        value={box.width_cm}
        onChange={e => onUpdate(index, "width_cm", parseFloat(e.target.value) || 0)}
      />
      <span className="text-slate-300">x</span>
      <Input
        type="number" min="0" step="0.1" placeholder="H"
        className="w-16 h-8 text-xs"
        value={box.height_cm}
        onChange={e => onUpdate(index, "height_cm", parseFloat(e.target.value) || 0)}
      />
      <span className="text-[10px] text-slate-400">cm</span>
      {showRemove && (
        <button onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function VendorRateCard({ vendor, index, isAdmin, adminData }) {
  const isCheapest = index === 0;
  const bd = vendor.charge_breakdown || {};
  const [showBreakdown, setShowBreakdown] = useState(isAdmin && bd.freight_total !== undefined);

  return (
    <div
      data-testid={`vendor-rate-${vendor.vendor_code}`}
      className={`border rounded-lg overflow-hidden transition-shadow hover:shadow-md ${
        isCheapest ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"
      }`}
    >
      <div className={`px-4 py-2.5 flex items-center justify-between ${
        isCheapest ? "bg-emerald-50" : "bg-slate-50"
      }`}>
        <div className="flex items-center gap-2">
          <Truck className={`w-4 h-4 ${isCheapest ? "text-emerald-600" : "text-slate-500"}`} />
          <span className="font-bold text-sm text-slate-800">{vendor.vendor_name}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 uppercase font-medium">
            {vendor.vendor_type}
          </span>
          {isCheapest && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">
              CHEAPEST
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>ETA: {vendor.sla_days} days</span>
          {isAdmin && bd.freight_total !== undefined && (
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-0.5"
              data-testid={`breakdown-toggle-${vendor.vendor_code}`}
            >
              <Info className="w-3 h-3" />
              {showBreakdown ? "Hide" : "Breakdown"}
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-2">
        {vendor.is_oda && vendor.account_type === "B2B" && (
          <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            <AlertTriangle className="w-3 h-3" />
            ODA Applicable — surcharge included
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-slate-500">Freight</span>
            <p className="text-base font-bold text-slate-800">{'\u20B9'}{vendor.customer_price}</p>
          </div>
          <div className="text-center">
            <span className="text-xs text-slate-500">GST @18%</span>
            <p className="text-base font-semibold text-amber-600">{'\u20B9'}{vendor.gst_amount}</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-500">Total</span>
            <p className="text-xl font-black text-emerald-700">{'\u20B9'}{vendor.final_price}</p>
          </div>
        </div>

        {/* Admin: Full Charge Breakdown */}
        {isAdmin && showBreakdown && bd.freight_total !== undefined && (
          <div className="mt-2 border border-orange-200 rounded-lg overflow-hidden" data-testid={`charge-breakdown-${vendor.vendor_code}`}>
            <div className="bg-orange-50 px-3 py-1.5 flex items-center gap-1 text-[10px] text-orange-700 font-bold border-b border-orange-200">
              <Eye className="w-3 h-3" /> Charge Breakdown — {vendor.account_type}
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-1 text-slate-500">Base Rate</td>
                  <td className="px-3 py-1 text-right font-mono font-medium">{'\u20B9'}{bd.base_rate}</td>
                </tr>
                {bd.additional_weight_charge > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1 text-slate-500">Additional Weight Charge</td>
                    <td className="px-3 py-1 text-right font-mono font-medium">{'\u20B9'}{bd.additional_weight_charge}</td>
                  </tr>
                )}
                <tr className="border-b border-slate-200 bg-slate-50">
                  <td className="px-3 py-1 font-medium text-slate-700">Freight Total (Vendor Cost)</td>
                  <td className="px-3 py-1 text-right font-mono font-bold text-red-600">{'\u20B9'}{bd.freight_total}</td>
                </tr>
                {bd.fuel_surcharge > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1 text-slate-500">Fuel Surcharge</td>
                    <td className="px-3 py-1 text-right font-mono">{'\u20B9'}{bd.fuel_surcharge}</td>
                  </tr>
                )}
                {bd.oda_surcharge > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1 text-slate-500">ODA Surcharge</td>
                    <td className="px-3 py-1 text-right font-mono">{'\u20B9'}{bd.oda_surcharge}</td>
                  </tr>
                )}
                {bd.handling_charge > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1 text-slate-500">Handling Charge</td>
                    <td className="px-3 py-1 text-right font-mono">{'\u20B9'}{bd.handling_charge}</td>
                  </tr>
                )}
                {bd.other_surcharges > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1 text-slate-500">Other Surcharges</td>
                    <td className="px-3 py-1 text-right font-mono">{'\u20B9'}{bd.other_surcharges}</td>
                  </tr>
                )}
                <tr className="border-b border-slate-200 bg-slate-50">
                  <td className="px-3 py-1 font-medium text-slate-700">Vendor Total (Cost + Surcharges)</td>
                  <td className="px-3 py-1 text-right font-mono font-bold text-slate-800">{'\u20B9'}{bd.vendor_total}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-1 text-slate-500">Margin ({bd.margin_percent}%)</td>
                  <td className="px-3 py-1 text-right font-mono text-blue-600">{'\u20B9'}{bd.margin_amount}</td>
                </tr>
                <tr className="border-b border-slate-200 bg-blue-50">
                  <td className="px-3 py-1 font-medium text-blue-700">Customer Subtotal</td>
                  <td className="px-3 py-1 text-right font-mono font-bold text-blue-700">{'\u20B9'}{bd.customer_subtotal}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-1 text-slate-500">GST @{bd.gst_rate}%</td>
                  <td className="px-3 py-1 text-right font-mono text-amber-600">{'\u20B9'}{bd.gst_amount}</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-3 py-1.5 font-bold text-emerald-800">Final Amount (incl. GST)</td>
                  <td className="px-3 py-1.5 text-right font-mono font-black text-emerald-800 text-sm">{'\u20B9'}{bd.final_amount}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Non-admin: Simple surcharge pills */}
        {!showBreakdown && vendor.surcharges?.length > 0 && (
          <div className="text-[10px] text-slate-400 flex gap-2 flex-wrap">
            {vendor.surcharges.map((s, i) => (
              <span key={i} className="bg-slate-100 px-1.5 py-0.5 rounded">
                {s.name || s.type}: {'\u20B9'}{s.amount}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RateCalculator() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const role = user?.role || "customer";
  const isAdmin = role === "admin";
  const isStaff = role === "staff" || role === "agent";

  const [pickupPin, setPickupPin] = useState("");
  const [deliveryPin, setDeliveryPin] = useState("");
  const [pickupInfo, setPickupInfo] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);

  const [boxes, setBoxes] = useState([{ length_cm: 0, width_cm: 0, height_cm: 0, quantity: 1 }]);
  const [weight, setWeight] = useState("");
  const [shipmentValue, setShipmentValue] = useState("");
  const [paymentMode, setPaymentMode] = useState("PREPAID");
  const [serviceType, setServiceType] = useState("SURFACE");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);

  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [rateView, setRateView] = useState("b2c");

  // Load customers for admin/staff
  useEffect(() => {
    if (isAdmin || isStaff) {
      fetch(`${API}/api/users?role=customer`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()).then(d => {
        if (Array.isArray(d)) setCustomers(d);
      }).catch(() => {});
    }
  }, [isAdmin, isStaff, token]);

  // Pincode lookup with debounce
  const lookupPincode = useCallback(async (pin, side) => {
    if (pin.length !== 6) {
      if (side === "pickup") setPickupInfo(null);
      else setDeliveryInfo(null);
      return;
    }
    const setLoading = side === "pickup" ? setPickupLoading : setDeliveryLoading;
    const setInfo = side === "pickup" ? setPickupInfo : setDeliveryInfo;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/v2/rate-calc/pincode-info/${pin}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setInfo(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => lookupPincode(pickupPin, "pickup"), 400);
    return () => clearTimeout(t);
  }, [pickupPin, lookupPincode]);

  useEffect(() => {
    const t = setTimeout(() => lookupPincode(deliveryPin, "delivery"), 400);
    return () => clearTimeout(t);
  }, [deliveryPin, lookupPincode]);

  const updateBox = (idx, field, val) => {
    setBoxes(prev => prev.map((b, i) => i === idx ? { ...b, [field]: val } : b));
  };
  const addBox = () => setBoxes(prev => [...prev, { length_cm: 0, width_cm: 0, height_cm: 0, quantity: 1 }]);
  const removeBox = (idx) => setBoxes(prev => prev.filter((_, i) => i !== idx));

  const calculate = async () => {
    if (!pickupPin || pickupPin.length !== 6) return toast.error("Enter valid 6-digit pickup pincode");
    if (!deliveryPin || deliveryPin.length !== 6) return toast.error("Enter valid 6-digit delivery pincode");
    if (!weight || parseFloat(weight) <= 0) return toast.error("Enter shipment weight");

    setCalculating(true);
    setResult(null);
    try {
      const payload = {
        pickup_pincode: pickupPin,
        delivery_pincode: deliveryPin,
        weight_kg: parseFloat(weight),
        boxes: boxes.filter(b => b.length_cm > 0 && b.width_cm > 0 && b.height_cm > 0),
        service_type: serviceType,
        payment_mode: paymentMode,
        shipment_value: shipmentValue ? parseFloat(shipmentValue) : null,
        customer_id: customerId || null,
      };
      const r = await fetch(`${API}/api/v2/rate-calc/calculate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const data = await r.json();
        setResult(data);
        // Auto-select the primary rate tab
        setRateView(data.primary_account_type === "B2B" ? "b2b" : "b2c");
        if ((data.b2c_rates?.length || 0) === 0 && (data.b2b_rates?.length || 0) === 0) {
          toast.warning("No vendor rates available for this route");
        }
      } else {
        const err = await r.json();
        toast.error(err.detail || "Calculation failed");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
    setCalculating(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5" data-testid="rate-calculator-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Serviceability & Rate Calculator</h1>
            <p className="text-xs text-slate-500">Check delivery rates across all vendor accounts</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* LEFT: Input Form */}
          <div className="lg:col-span-3 space-y-4">
            {/* Pincode Section */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                Pickup & Delivery Pincode
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                <PincodeInput
                  label="Pickup" side="pickup"
                  value={pickupPin} onChange={setPickupPin}
                  pincodeInfo={pickupInfo} loading={pickupLoading}
                />
                <div className="flex items-center gap-1 text-slate-300">
                  <div className="w-4 border-t border-slate-300" />
                  <ArrowRight className="w-4 h-4" />
                  <div className="w-4 border-t border-slate-300" />
                </div>
                <PincodeInput
                  label="Delivery" side="delivery"
                  value={deliveryPin} onChange={setDeliveryPin}
                  pincodeInfo={deliveryInfo} loading={deliveryLoading}
                />
              </div>
              {/* ODA indicator */}
              {deliveryInfo?.is_oda && (
                <div data-testid="oda-indicator" className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md w-fit">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span className="font-semibold">ODA Applicable</span> — Delivery pincode is Out of Delivery Area
                </div>
              )}
              {pickupInfo?.is_oda && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md w-fit">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span className="font-semibold">Pickup ODA</span>
                </div>
              )}
              {deliveryInfo?.found && !deliveryInfo.is_oda && deliveryPin.length === 6 && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Delivery area serviceable
                </div>
              )}
            </div>

            {/* Box Dimensions */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                Box Dimensions
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[10px] text-slate-400 font-medium px-1">
                  <span className="w-6"></span>
                  <span className="w-16 text-center">Qty</span>
                  <span className="w-16 text-center">L</span>
                  <span className="w-3"></span>
                  <span className="w-16 text-center">B</span>
                  <span className="w-3"></span>
                  <span className="w-16 text-center">H</span>
                </div>
                {boxes.map((box, i) => (
                  <BoxRow
                    key={i} box={box} index={i}
                    onUpdate={updateBox} onRemove={removeBox}
                    showRemove={boxes.length > 1}
                  />
                ))}
              </div>
              <button
                onClick={addBox}
                className="mt-3 flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                data-testid="add-box-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add Another Box Size
              </button>
            </div>

            {/* Weight & Value */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Total Shipment Weight
                  </label>
                  <div className="relative">
                    <Input
                      data-testid="calc-weight"
                      type="number" step="0.1" min="0.1"
                      value={weight} onChange={e => setWeight(e.target.value)}
                      placeholder="Enter shipment weight"
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium">
                      Kg
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Shipment Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{'\u20B9'}</span>
                    <Input
                      data-testid="calc-shipment-value"
                      type="number" step="1" min="0"
                      value={shipmentValue} onChange={e => setShipmentValue(e.target.value)}
                      placeholder="Enter Shipment Value"
                      className="pl-7"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Options Row */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {/* Payment Mode */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Payment Mode
                  </label>
                  <div className="space-y-1.5">
                    {["PREPAID", "COD"].map(mode => (
                      <label key={mode} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio" name="payment_mode"
                          checked={paymentMode === mode}
                          onChange={() => setPaymentMode(mode)}
                          className="accent-indigo-600"
                          data-testid={`calc-payment-${mode.toLowerCase()}`}
                        />
                        <span className="text-sm text-slate-700">
                          {mode === "PREPAID" ? "Prepaid" : "Collect on Delivery (COD)"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Service Type
                  </label>
                  <div className="space-y-1.5">
                    {["SURFACE", "EXPRESS", "AIR"].map(st => (
                      <label key={st} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio" name="service_type"
                          checked={serviceType === st}
                          onChange={() => setServiceType(st)}
                          className="accent-indigo-600"
                          data-testid={`calc-service-${st.toLowerCase()}`}
                        />
                        <span className="text-sm text-slate-700">{st.charAt(0) + st.slice(1).toLowerCase()}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Customer Selector (Admin/Staff only) */}
                {(isAdmin || isStaff) && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Customer Account
                    </label>
                    <select
                      data-testid="calc-customer-select"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                      value={customerId}
                      onChange={e => setCustomerId(e.target.value)}
                    >
                      <option value="">Global Rate</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Calculate Button */}
            <Button
              data-testid="calc-submit-btn"
              onClick={calculate}
              disabled={calculating}
              className="w-full h-11 text-sm font-bold bg-indigo-600 hover:bg-indigo-700"
            >
              {calculating ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Calculating Rates...</>
              ) : (
                <><Calculator className="w-4 h-4 mr-2" /> Calculate Rate</>
              )}
            </Button>
          </div>

          {/* RIGHT: Results Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-xl sticky top-4 overflow-hidden">
              {!result ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Package className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-base font-bold text-slate-400 mb-2">
                    The calculated rates appear here
                  </h3>
                  <p className="text-xs text-slate-400 max-w-[260px]">
                    This freight charge is subject to change based on revisions in weight & dimension measurements, delivery pin code and pricing file.
                  </p>
                </div>
              ) : (
                <div data-testid="calc-result">
                  {/* Route Summary Header */}
                  <div className="bg-indigo-600 text-white px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="font-bold">{result.pickup_city || result.pickup_pincode}</span>
                        <ArrowRight className="w-3 h-3 opacity-60" />
                        <span className="font-bold">{result.delivery_city || result.delivery_pincode}</span>
                      </div>
                      {result.zone_code && (
                        <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-mono font-bold">
                          {result.zone_code}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 text-[10px] opacity-80">
                      {result.distance_km && <span>{result.distance_km} km</span>}
                      <span>ETA: {result.eta_days} days</span>
                      <span>{result.service_type}</span>
                      <span>{result.payment_mode}</span>
                    </div>
                  </div>

                  {/* Weight + Account Type */}
                  <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                    <div className="grid grid-cols-4 gap-2 text-[11px]">
                      <div>
                        <span className="text-slate-400">Dead Wt</span>
                        <p className="font-bold text-slate-700">{result.dead_weight_kg} kg</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Vol Wt</span>
                        <p className="font-bold text-slate-700">{result.volumetric_weight_kg} kg</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Chargeable</span>
                        <p className="font-bold text-indigo-600">{result.chargeable_weight_kg} kg</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Auto-Detect</span>
                        <p className={`font-bold ${result.primary_account_type === 'B2C' ? 'text-blue-600' : 'text-purple-600'}`}>
                          {result.primary_account_type} {result.chargeable_weight_kg <= 20 ? '(≤20kg)' : '(>20kg)'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ODA Flag in results */}
                  {result.is_delivery_oda && (
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span className="font-semibold">ODA Applicable</span> — ODA surcharge applied
                    </div>
                  )}

                  {/* COD info */}
                  {result.cod_charge > 0 && (
                    <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex items-center justify-between text-xs">
                      <span className="text-purple-700 font-medium">COD Charge</span>
                      <span className="font-bold text-purple-800">{'\u20B9'}{result.cod_charge}</span>
                    </div>
                  )}

                  {/* B2C / B2B Rate Tabs */}
                  <div className="p-4 space-y-3">
                    <div className="flex gap-1 mb-2">
                      <button
                        onClick={() => setRateView("b2c")}
                        data-testid="rate-view-b2c"
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                          rateView === "b2c"
                            ? "bg-blue-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        B2C Rates ({result.b2c_rates?.length || 0})
                      </button>
                      <button
                        onClick={() => setRateView("b2b")}
                        data-testid="rate-view-b2b"
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                          rateView === "b2b"
                            ? "bg-purple-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        B2B Rates ({result.b2b_rates?.length || 0})
                      </button>
                    </div>

                    {rateView === "b2c" && result.b2c_rates?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] text-blue-500 font-medium">
                          {result.primary_account_type === "B2C" ? "Recommended (≤20kg)" : "Alternative rate card"}
                        </p>
                        {result.b2c_rates.map((v, i) => (
                          <VendorRateCard key={`b2c-${v.vendor_code}`} vendor={v} index={i} isAdmin={isAdmin} adminData={result.admin_view} />
                        ))}
                      </div>
                    )}
                    {rateView === "b2c" && (!result.b2c_rates || result.b2c_rates.length === 0) && (
                      <div className="text-center py-4 text-xs text-slate-400">No B2C rates available</div>
                    )}

                    {rateView === "b2b" && result.b2b_rates?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] text-purple-500 font-medium">
                          {result.primary_account_type === "B2B" ? "Recommended (>20kg)" : "Alternative rate card"}
                        </p>
                        {result.b2b_rates.map((v, i) => (
                          <VendorRateCard key={`b2b-${v.vendor_code}`} vendor={v} index={i} isAdmin={isAdmin} adminData={result.admin_view} />
                        ))}
                      </div>
                    )}
                    {rateView === "b2b" && (!result.b2b_rates || result.b2b_rates.length === 0) && (
                      <div className="text-center py-4 text-xs text-slate-400">No B2B rates available</div>
                    )}
                  </div>

                  {/* Disclaimer */}
                  <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100">
                    <div className="flex items-start gap-1.5 text-[10px] text-slate-400">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>Rates include 18% GST. ≤20kg uses B2C rate card, &gt;20kg uses B2B rate card. {isAdmin ? "Click 'Breakdown' on each vendor card for full charge details." : "Subject to weight verification at hub."}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
